import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Divider
} from '@material-ui/core'
import {
  Close as CloseIcon,
  Schedule as ScheduleIcon
} from '@material-ui/icons'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import * as iotAPI from '../../data/iot'

const useStyles = makeStyles(theme => ({
  dialog: {
    minWidth: 500
  },
  formField: {
    marginBottom: theme.spacing(2)
  },
  deviceInfo: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  extensionOptions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  extensionButton: {
    minWidth: 80
  }
}))

const EXTENSION_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' }
]

export const IoTTokenExtensionModal = ({ open, onClose, device, modalityId, onTokenExtended }) => {
  const classes = useStyles()
  const [extensionDays, setExtensionDays] = useState(30)
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [tokenStatus, setTokenStatus] = useState(null)

  useEffect(() => {
    if (open && device && modalityId) {
      fetchTokenStatus()
    }
  }, [open, device, modalityId])

  const fetchTokenStatus = async () => {
    try {
      const status = await iotAPI.getTokenStatus(modalityId, device.id)
      setTokenStatus(status)
    } catch (err) {
      console.error('Error fetching token status:', err)
      // Don't show error for token status, just continue
    }
  }

  const handleSubmit = async () => {
    if (!device || !modalityId || !extensionDays || !reason.trim()) return

    setLoading(true)
    setError(null)
    setSuccess(false)

    try {
      const extensionData = {
        extensionDays,
        reason: reason.trim()
      }

      const result = await iotAPI.extendToken(modalityId, device.id, extensionData)
      
      setSuccess(true)
      
      // Call the callback to refresh the device list
      if (onTokenExtended) {
        onTokenExtended()
      }
    } catch (err) {
      console.error('Error extending token:', err)
      setError(err.message || 'Failed to extend token')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setExtensionDays(30)
    setReason('')
    setError(null)
    setSuccess(false)
    setTokenStatus(null)
    onClose()
  }

  const handleQuickExtension = (days) => {
    setExtensionDays(days)
  }

  if (!device) return null

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className={classes.dialog}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <ScheduleIcon style={{ marginRight: 8 }} />
            <Typography variant="h6">
              Extend Device Token
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {success ? (
          <Box>
            <Alert severity="success" style={{ marginBottom: 16 }}>
              Token has been successfully extended!
            </Alert>
            
            <Typography variant="body2" color="textSecondary" gutterBottom>
              The device token has been extended by <strong>{extensionDays} days</strong>.
            </Typography>
            
            {tokenStatus && (
              <Typography variant="body2" color="textSecondary">
                New expiration date: <strong>{new Date(tokenStatus.expiresAt).toLocaleString()}</strong>
              </Typography>
            )}
          </Box>
        ) : (
          <Box>
            {error && (
              <Alert severity="error" style={{ marginBottom: 16 }}>
                {error}
              </Alert>
            )}

            <Box className={classes.deviceInfo}>
              <Typography variant="subtitle2" gutterBottom>
                Device Information:
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Device ID:</strong> {device.deviceId}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Device Type:</strong> {device.deviceType}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Location:</strong> {device.location || 'N/A'}
              </Typography>
              {tokenStatus && (
                <Typography variant="body2" color="textSecondary">
                  <strong>Current Expiration:</strong> {new Date(tokenStatus.expiresAt).toLocaleString()}
                </Typography>
              )}
            </Box>

            <Divider style={{ margin: '16px 0' }} />

            <Typography variant="subtitle2" gutterBottom>
              Extension Period:
            </Typography>
            
            <Box className={classes.extensionOptions}>
              {EXTENSION_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  variant={extensionDays === option.value ? 'contained' : 'outlined'}
                  color="primary"
                  size="small"
                  className={classes.extensionButton}
                  onClick={() => handleQuickExtension(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </Box>

            <FormControl fullWidth className={classes.formField}>
              <InputLabel>Extension Days</InputLabel>
              <Select
                value={extensionDays}
                onChange={(e) => setExtensionDays(e.target.value)}
              >
                {EXTENSION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Reason for Extension"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Device maintenance, extended monitoring period"
              className={classes.formField}
              multiline
              rows={3}
              required
            />

            <Alert severity="info">
              <Typography variant="body2">
                <strong>Note:</strong> Token extensions are logged for audit purposes. 
                The device will continue to function normally with the extended token.
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {success ? (
          <Button onClick={handleClose} color="primary" variant="contained">
            Close
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={!extensionDays || !reason.trim() || loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Extending...' : 'Extend Token'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default IoTTokenExtensionModal
